import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { URL } from "constants/api/requests"
import { ALL_LANGUAGE_CODES, DEFAULT_LOCALE, getLanguageName } from "constants/languages"

import { promotionsService } from "services/promotions/promotions.service"
import type { PromotionDetail, PromotionPayload, PromotionTranslationPayload } from "services/promotions/type"

import { uploadFile } from "util/api"

import { EMPTY_PROMOTION_FORM, emptyTranslation } from "../constants"
import { PromotionFormState, TranslationForm } from "../types"

export type EditorMode = "create" | "edit"

/**
 * Where to go once the promotion is saved. Supplied when the editor is embedded
 * in the CMS Pages view, which returns to its own list rather than routing away
 * to the standalone screen the editor was originally built for.
 */
export function usePromotionEditor(mode: EditorMode, id?: string, onSaved?: () => void) {
  const navigate = useNavigate()

  const [activeLocale, setActiveLocale] = useState<string>(DEFAULT_LOCALE)
  const [form, setForm] = useState<PromotionFormState>(() => structuredClone(EMPTY_PROMOTION_FORM))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [slugEdited, setSlugEdited] = useState(false)

  const localeOptions = useMemo(() => {
    const codes = (ALL_LANGUAGE_CODES || []).filter(Boolean)
    return codes
      .map((code) => ({ value: code, name: getLanguageName(code) }))
      .sort((a, b) => {
        if (a.value === DEFAULT_LOCALE) return -1
        if (b.value === DEFAULT_LOCALE) return 1
        return a.name.localeCompare(b.name)
      })
  }, [])

  const addedLocales = useMemo(() => {
    const keys = Object.keys(form.translations || {})
    const rest = keys.filter((k) => k !== DEFAULT_LOCALE)
    rest.sort((a, b) => getLanguageName(a).localeCompare(getLanguageName(b)))
    return [DEFAULT_LOCALE, ...rest].filter((l, i, arr) => arr.indexOf(l) === i)
  }, [form.translations])

  const availableToAdd = useMemo(() => {
    const set = new Set(Object.keys(form.translations || {}))
    return localeOptions.filter((o) => !set.has(o.value) && o.value !== DEFAULT_LOCALE)
  }, [form.translations, localeOptions])

  const tr: TranslationForm = form.translations[activeLocale] ?? emptyTranslation()

  // ---------- helpers ----------
  const setTranslationField = (locale: string, patch: Partial<TranslationForm>) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [locale]: {
          ...(prev.translations[locale] ?? emptyTranslation()),
          ...patch
        }
      }
    }))
  }

  const addLocale = (nextLocaleRaw: string) => {
    const nextLocale = (nextLocaleRaw || "").trim().toLowerCase()
    if (!nextLocale) return

    if (!ALL_LANGUAGE_CODES.includes(nextLocale as any)) {
      toast.error("Invalid locale")
      return
    }

    setForm((prev) => {
      if (prev.translations[nextLocale]) return prev
      return {
        ...prev,
        translations: { ...prev.translations, [nextLocale]: emptyTranslation() }
      }
    })

    setActiveLocale(nextLocale)
  }

  const removeLocale = (locale: string) => {
    if (locale === DEFAULT_LOCALE) return

    setForm((prev) => {
      if (!prev.translations[locale]) return prev
      const next = { ...prev.translations }
      delete next[locale]
      return { ...prev, translations: next }
    })

    setActiveLocale((cur) => (cur === locale ? DEFAULT_LOCALE : cur))
  }

  const buildTranslationsPayload = async (): Promise<PromotionTranslationPayload[]> => {
    const out: PromotionTranslationPayload[] = []

    for (const [locale, t] of Object.entries(form.translations)) {
      const title = (t.title ?? "").trim()
      const short_title = t.short_title?.trim() || undefined
      const description = t.description?.trim() || undefined
      const blocks = t.blocks ?? []

      let banner_image: string | null | undefined
      let thumbnail_image: string | null | undefined

      if (typeof t.banner_image === "string") banner_image = t.banner_image || null
      else if (t.banner_image instanceof File) banner_image = await uploadFile(t.banner_image)
      else banner_image = null

      if (typeof t.thumbnail_image === "string") thumbnail_image = t.thumbnail_image || null
      else if (t.thumbnail_image instanceof File) thumbnail_image = await uploadFile(t.thumbnail_image)
      else thumbnail_image = null

      const hasAny = title.length > 0 || (description?.length ?? 0) > 0 || (blocks?.length ?? 0) > 0 || !!banner_image || !!thumbnail_image

      if (!hasAny) continue

      out.push({
        locale,
        title,
        short_title,
        description,
        blocks,
        banner_image,
        thumbnail_image
      })
    }

    return out
  }

  // ---------- load (edit mode) ----------
  useEffect(() => {
    if (mode !== "edit" || !id) return

    const load = async () => {
      try {
        setLoading(true)
        const full: PromotionDetail = await promotionsService.getPromotionById(id)

        const map: Record<string, TranslationForm> = {
          [DEFAULT_LOCALE]: emptyTranslation()
        }

        for (const t of full.translations ?? []) {
          if (!t?.locale) continue
          map[t.locale] = {
            title: t.title ?? "",
            short_title: t.short_title ?? "",
            description: t.description ?? "",
            banner_image: t.banner_image ?? null,
            thumbnail_image: t.thumbnail_image ?? null,
            blocks: (t.blocks ?? []) as any
          }
        }

        setForm({
          slug: full.slug,
          categories: full.categories,
          status: full.status,
          start_at: full.start_at ?? null,
          end_at: full.end_at ?? null,
          is_active: full.is_active,
          is_featured: full.is_featured,
          game_ids: full.games?.map((g) => g.game_id) ?? [],
          translations: map
        })

        setSlugEdited(true)
        setActiveLocale(map[DEFAULT_LOCALE] ? DEFAULT_LOCALE : Object.keys(map)[0] ?? DEFAULT_LOCALE)
      } catch (err: any) {
        toast.error(err?.message ?? "Failed to load promotion")
      } finally {
        setLoading(false)
      }
    }

    void load()
  }, [mode, id])

  // ---------- submit ----------
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedSlug = form.slug.trim()
    if (!trimmedSlug) return toast.error("Slug is required")

    const translations = await buildTranslationsPayload()
    const defaultTr = translations.find((t) => t.locale === DEFAULT_LOCALE)
    if (!defaultTr?.title?.trim()) return toast.error(`${getLanguageName(DEFAULT_LOCALE)} Title is required`)

    try {
      setSaving(true)

      const payload: PromotionPayload = {
        slug: trimmedSlug,
        categories: form.categories,
        status: form.status,
        start_at: form.start_at ?? null,
        end_at: form.end_at ?? null,
        is_active: form.is_active,
        is_featured: form.is_featured,
        game_ids: form.game_ids ?? [],
        translations
      }

      if (mode === "create") {
        await promotionsService.createPromotion(payload)
        toast.success("Promotion created")
      } else if (mode === "edit" && id) {
        await promotionsService.updatePromotion(id, payload)
        toast.success("Promotion updated")
      }

      if (onSaved) onSaved()
      else navigate(URL.PROMOTIONS)
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to save promotion")
    } finally {
      setSaving(false)
    }
  }

  return {
    // state
    form,
    setForm,
    tr,
    activeLocale,
    setActiveLocale,
    loading,
    saving,
    slugEdited,
    setSlugEdited,

    // derived
    localeOptions,
    addedLocales,
    availableToAdd,

    // actions
    setTranslationField,
    addLocale,
    removeLocale,
    submit
  }
}
